import { formatAssetData, formatPusherAssetData } from 'api/services/AssetsService';
import PublicContext from 'apps/canvas/canvas/canvas-with-public-context/public-context/PublicContext';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { ACCOUNT, PAYMENT_SUCCESSFUL } from 'core/navigation/Routes';
import { handleProductPhotoAssetFromPusherAsync } from 'core/store/middleware/StudioCanvasMiddleware';

import {
	pusherSubscriptionStatusSelector,
	setPusherSubscriptionStatus,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import { handleAssetFromPusher } from 'core/store/reducers/assetsReducer/AssetsSlice';
import {
	handelCanvasImagesErrorFromSocket,
	handelCanvasMediaErrorFromSocket,
	loadCanvasFeedImageFromSocket,
	loadCanvasFeedMediaFromSocket,
	loadCharacterMediaErrorFromSocket,
	loadCharacterMediaFromSocket,
} from 'core/store/reducers/canvasReducer/CanvasSliceThunk';
import { handleCharacterAssetFromPusher } from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import { loadMusicVideoScenesMediaFromSocket } from 'core/store/reducers/intermediateMediaReducer/IntermediateMediaSliceThunk';
import { setGetPayLinkAPIStage } from 'core/store/reducers/profileReducer/ProfileSlice';
import {
	handelStudioImagesErrorFromSocket,
	handelStudioMediaErrorFromSocket,
	loadStudioFeedImageFromSocket,
	loadStudioFeedMediaFromSocket,
} from 'core/store/reducers/studioReducer/StudioSliceThunk';
import Pusher from 'pusher-js';
import { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const usePusher = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const userProfile = useSelector(userProfileSelector);

	const publicContext = useContext(PublicContext);
	const isPublicMode = publicContext?.isPublicMode;

	const userId = userProfile?.user_id;
	const pusherSubscriptionStatus = useSelector(pusherSubscriptionStatusSelector);

	const pusher = useMemo(
		() =>
			new Pusher(process.env.REACT_APP_PUSHER_APP_KEY, {
				cluster: 'mt1',
			}),
		[],
	);

	useEffect(() => {
		if (userId && pusherSubscriptionStatus !== ApiStage.SUCCESS && !isPublicMode) {
			// INSTANTIATE PUSHER
			Pusher.logToConsole = JSON.parse(process.env.REACT_APP_PUSHER_LOG_TO_CONSOLE ?? false);

			// PUSHER SUBSCRIBE TO CHANNEL
			const mychannel = `channel_${userId}`;
			const channel = pusher.subscribe(mychannel);

			// PUSHER EVENTS BIND
			channel.bind('pusher:subscription_succeeded', () => {
				dispatch(setPusherSubscriptionStatus(ApiStage.SUCCESS));
			});
			channel.bind('subscription_payment', (data) => {
				if (data.status === 'success') {
					navigate(`/${ACCOUNT}/${PAYMENT_SUCCESSFUL}`);
				} else {
					dispatch(setGetPayLinkAPIStage(ApiStage.ERROR));
				}
			});

			channel.bind('new_images', (data) => {
				// success event
				// show images
				if (data.playground === 'studio') {
					dispatch(loadStudioFeedImageFromSocket(data));
				} else if (data.playground === 'canvas') {
					dispatch(loadCanvasFeedImageFromSocket(data));
				}
			});
			channel.bind('images_error', (data) => {
				// error event
				// show error
				if (data.playground === 'studio') {
					dispatch(handelStudioImagesErrorFromSocket(data));
				} else if (data.playground === 'canvas') {
					dispatch(handelCanvasImagesErrorFromSocket(data));
				}
			});

			channel.bind('media_success', (data) => {
				if (data.playground === 'studio') {
					dispatch(loadStudioFeedMediaFromSocket(data));
				} else if (data.playground === 'canvas') {
					dispatch(loadCanvasFeedMediaFromSocket(data));
				} else if (data.playground === 'character_widget') {
					dispatch(loadCharacterMediaFromSocket(data));
				}
			});
			channel.bind('media_failure', (data) => {
				if (data.playground === 'studio') {
					dispatch(handelStudioMediaErrorFromSocket(data));
				} else if (data.playground === 'canvas') {
					dispatch(handelCanvasMediaErrorFromSocket(data));
				} else if (data.playground === 'character_widget') {
					dispatch(loadCharacterMediaErrorFromSocket(data));
				}
			});

			channel.bind('new_asset', (data) => {
				dispatch(handleAssetFromPusher(formatPusherAssetData(data)));
				dispatch(handleCharacterAssetFromPusher(formatPusherAssetData(data)));

				dispatch(handleProductPhotoAssetFromPusherAsync(formatPusherAssetData(data)));
			});
			channel.bind('new_asset_with_metadata', (data) => {
				dispatch(handleAssetFromPusher(formatAssetData(data)));
			});

			channel.bind('intermediate_media', (data) => {
				if (data.gen_type === 'music_video') {
					dispatch(loadMusicVideoScenesMediaFromSocket(data));
				}
			});
		}
	}, [userId, pusherSubscriptionStatus, pusher, dispatch, navigate, isPublicMode]);
};
